<template>
    <nav aria-label="Progress" class="hidden @container md:block">
        <ol role="list" class="ml-0 flex list-none items-center justify-center pb-12 pt-6">
            <li
                v-for="(step, stepIdx) in steps"
                :key="step.name"
                :class="[
                    stepIdx !== steps.length - 1
                        ? 'supports-not-cq:md:pr-30 supports-cq:@md:pr-30 pr-14 supports-cq:@sm:pr-20 supports-cq:@lg:pr-36 supports-not-cq:sm:pr-20 supports-not-cq:lg:pr-40'
                        : '',
                    'relative',
                ]">
                <template v-if="isCompletedStep(step)">
                    <div class="absolute inset-0 flex items-center" aria-hidden="true">
                        <div class="h-0.5 w-full bg-primary" />
                    </div>
                    <button
                        class="relative flex h-8 w-8 items-center justify-center rounded-full bg-primary hover:bg-primary-800"
                        @click="navigateToStep(step)">
                        <CheckIcon class="h-5 w-5 text-white" aria-hidden="true" />
                        <span class="sr-only">{{ step.label }}</span>
                    </button>
                </template>
                <template v-else-if="isCurrentStep(step)">
                    <div class="absolute inset-0 flex items-center" aria-hidden="true">
                        <div class="h-0.5 w-full bg-gray-200" />
                    </div>
                    <a
                        href="#"
                        class="relative flex h-8 w-8 items-center justify-center rounded-full border-2 border-primary bg-white"
                        aria-current="step">
                        <span class="h-2.5 w-2.5 rounded-full bg-primary" aria-hidden="true" />
                        <span class="sr-only">{{ step.label }}</span>
                    </a>
                </template>
                <template v-else>
                    <div class="absolute inset-0 flex items-center" aria-hidden="true">
                        <div class="h-0.5 w-full bg-gray-200" />
                    </div>
                    <a
                        href="#"
                        class="group relative flex h-8 w-8 items-center justify-center rounded-full border-2 border-gray-300 bg-white hover:border-gray-400">
                        <span
                            class="h-2.5 w-2.5 rounded-full bg-transparent group-hover:bg-gray-300"
                            aria-hidden="true" />
                        <span class="sr-only">{{ step.label }}</span>
                    </a>
                </template>
                <span
                    :class="[
                        'absolute flex w-[32px] justify-center pt-2 text-xs text-primary',
                        { 'font-bold': isCurrentStep(step) },
                    ]">
                    <span>{{ step.label }}</span></span
                >
            </li>
        </ol>
    </nav>
</template>

<script setup lang="ts">
import { CheckIcon } from '@heroicons/vue/solid'
import { InvestmentStep, useInvestmentProcessNavigation } from '../../utilities/account/useInvestmentProcessNavigation'
import { storeToRefs } from 'pinia'
import { onMounted } from 'vue'
import { getPendingInvestmentCookie } from '../../utilities/investment-process/usePendingInvestmentProcess'
import { useInvestmentProcessDataState } from '../../utilities/investment-process/useInvestmentProcess'

const state = useInvestmentProcessNavigation()
const { steps, pageKey } = storeToRefs(state)

function isCurrentStep(step: InvestmentStep) {
    return step.name === pageKey.value
}

function navigateToStep(step: InvestmentStep) {
    pageKey.value = step.name
}

function isCompletedStep(step: InvestmentStep) {
    const currentStepIndex = state.steps.findIndex((iteratingStep) => pageKey.value === iteratingStep.name)
    const stepIndex = state.steps.indexOf(step)

    return currentStepIndex > stepIndex
}

function checkIPCookieAndSetBaseStepIfNeeded() {
    if (getPendingInvestmentCookie()) {
        useInvestmentProcessDataState().loadPendingInvestment()
        navigateToStep(useInvestmentProcessNavigation().steps[getPendingInvestmentCookie().step])
    }
}

onMounted(() => {
    checkIPCookieAndSetBaseStepIfNeeded()
})
</script>
